import { fromDatetimeLocal, toDatetimeLocalValue } from '@/components/event-wizard/wizard-utils';

export type RegistrationSettingsState = {
  approval_required: boolean;
  waitlist_enabled: boolean;
  allow_duplicate_registrations: boolean;
  submission_required: boolean;
  submission_deadline: string;
  file_upload_allowed: boolean;
  submission_instructions: string;
};

export type TeamSettingsState = {
  min_size: number;
  max_size: number;
};

export type EligibilitySettingsState = {
  min_age: string;
  max_age: string;
  institutions: string[];
  email_domains: string[];
  countries: string[];
};

export type ParticipationFormState = {
  registration_type: 'individual' | 'team';
  registration: RegistrationSettingsState;
  team: TeamSettingsState;
  eligibility: EligibilitySettingsState;
  advanced_json: string;
};

export type ParticipationFieldErrors = {
  team_min?: string;
  team_max?: string;
  min_age?: string;
  max_age?: string;
  submission_deadline?: string;
  advanced_json?: string;
};

export function emptyParticipationForm(): ParticipationFormState {
  return {
    registration_type: 'individual',
    registration: {
      approval_required: false,
      waitlist_enabled: false,
      allow_duplicate_registrations: false,
      submission_required: false,
      submission_deadline: '',
      file_upload_allowed: false,
      submission_instructions: '',
    },
    team: { min_size: 1, max_size: 5 },
    eligibility: {
      min_age: '',
      max_age: '',
      institutions: [],
      email_domains: [],
      countries: [],
    },
    advanced_json: '',
  };
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((v) => String(v).trim()).filter(Boolean);
}

function asBool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

export function participationFromEvent(
  event: Record<string, unknown> | null | undefined,
): ParticipationFormState {
  const base = emptyParticipationForm();
  if (!event) return base;

  const settings = (event.settings ?? {}) as Record<string, unknown>;
  const reg = (settings.registration ?? {}) as Record<string, unknown>;
  const team = (settings.team ?? {}) as Record<string, unknown>;
  const eligibility = (settings.eligibility ?? {}) as Record<string, unknown>;
  const submissionRules = event.submission_rules ?? settings.submission_rules;

  const submissionRequired =
    asBool(reg.submission_required) ||
    asBool((submissionRules as Record<string, unknown> | null)?.required);

  return {
    registration_type: event.registration_type === 'team' ? 'team' : 'individual',
    registration: {
      approval_required: asBool(reg.approval_required),
      waitlist_enabled: asBool(reg.waitlist_enabled),
      allow_duplicate_registrations: asBool(reg.allow_duplicate_registrations),
      submission_required: submissionRequired,
      submission_deadline: toDatetimeLocalValue(
        (reg.submission_deadline as string | undefined) ??
          ((submissionRules as Record<string, unknown> | null)?.deadline as string | undefined),
      ),
      file_upload_allowed: asBool(reg.file_upload_allowed) ||
        asBool((submissionRules as Record<string, unknown> | null)?.file_upload_allowed),
      submission_instructions:
        typeof reg.submission_instructions === 'string'
          ? reg.submission_instructions
          : typeof (submissionRules as Record<string, unknown> | null)?.instructions === 'string'
            ? ((submissionRules as Record<string, unknown>).instructions as string)
            : '',
    },
    team: {
      min_size: typeof team.min_size === 'number' ? team.min_size : 1,
      max_size: typeof team.max_size === 'number' ? team.max_size : 5,
    },
    eligibility: {
      min_age: eligibility.min_age != null ? String(eligibility.min_age) : '',
      max_age: eligibility.max_age != null ? String(eligibility.max_age) : '',
      institutions: asStringArray(eligibility.institutions),
      email_domains: asStringArray(eligibility.email_domains),
      countries: asStringArray(eligibility.countries),
    },
    advanced_json:
      settings.advanced_participation && typeof settings.advanced_participation === 'object'
        ? JSON.stringify(settings.advanced_participation, null, 2)
        : '',
  };
}

export function buildParticipationSettingsPayload(form: ParticipationFormState): {
  settings: Record<string, unknown>;
  registration_type: 'individual' | 'team';
  submission_rules?: Record<string, unknown>;
} {
  const registration: Record<string, unknown> = {
    approval_required: form.registration.approval_required,
    waitlist_enabled: form.registration.waitlist_enabled,
    allow_duplicate_registrations: form.registration.allow_duplicate_registrations,
    submission_required: form.registration.submission_required,
    file_upload_allowed: form.registration.file_upload_allowed,
    submission_instructions: form.registration.submission_instructions.trim() || null,
  };

  if (form.registration.submission_required && form.registration.submission_deadline) {
    registration.submission_deadline = fromDatetimeLocal(form.registration.submission_deadline);
  }

  const eligibility: Record<string, unknown> = {};
  if (form.eligibility.min_age.trim()) eligibility.min_age = Number(form.eligibility.min_age);
  if (form.eligibility.max_age.trim()) eligibility.max_age = Number(form.eligibility.max_age);
  if (form.eligibility.institutions.length) eligibility.institutions = form.eligibility.institutions;
  if (form.eligibility.email_domains.length) eligibility.email_domains = form.eligibility.email_domains;
  if (form.eligibility.countries.length) eligibility.countries = form.eligibility.countries;

  let advanced_participation: Record<string, unknown> | undefined;
  if (form.advanced_json.trim()) {
    try {
      const parsed = JSON.parse(form.advanced_json.trim());
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        advanced_participation = parsed as Record<string, unknown>;
      } else {
        throw new Error('invalid');
      }
    } catch {
      throw new Error('Advanced configuration must be valid JSON.');
    }
  }

  const submission_rules: Record<string, unknown> | undefined = form.registration.submission_required
    ? {
        required: true,
        deadline: fromDatetimeLocal(form.registration.submission_deadline),
        file_upload_allowed: form.registration.file_upload_allowed,
        instructions: form.registration.submission_instructions.trim() || null,
      }
    : undefined;

  return {
    registration_type: form.registration_type,
    settings: {
      registration,
      team: {
        min_size: form.team.min_size,
        max_size: form.team.max_size,
      },
      eligibility,
      ...(advanced_participation ? { advanced_participation } : {}),
    },
    submission_rules,
  };
}

export function validateParticipationForm(
  form: ParticipationFormState,
  opts?: { teamAllowed?: boolean },
): ParticipationFieldErrors {
  const errors: ParticipationFieldErrors = {};
  const teamAllowed = opts?.teamAllowed !== false;

  if (teamAllowed && form.registration_type === 'team') {
    if (form.team.min_size < 1) {
      errors.team_min = 'Minimum team size must be at least 1.';
    }
    if (form.team.max_size < form.team.min_size) {
      errors.team_max = 'Maximum team size must be greater than or equal to minimum.';
    }
  }

  const minAge = form.eligibility.min_age.trim() ? Number(form.eligibility.min_age) : null;
  const maxAge = form.eligibility.max_age.trim() ? Number(form.eligibility.max_age) : null;
  if (minAge != null && (Number.isNaN(minAge) || minAge < 0)) {
    errors.min_age = 'Minimum age must be a valid number.';
  }
  if (maxAge != null && (Number.isNaN(maxAge) || maxAge < 0)) {
    errors.max_age = 'Maximum age must be a valid number.';
  }
  if (minAge != null && maxAge != null && minAge > maxAge) {
    errors.max_age = 'Maximum age must be greater than or equal to minimum age.';
  }

  if (form.registration.submission_required && !form.registration.submission_deadline.trim()) {
    errors.submission_deadline = 'Submission deadline is required when submissions are enabled.';
  }

  if (form.advanced_json.trim()) {
    try {
      const parsed = JSON.parse(form.advanced_json);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        errors.advanced_json = 'Additional settings must be a JSON object.';
      }
    } catch (err) {
      const message = err instanceof SyntaxError ? err.message : 'Invalid JSON.';
      const positionMatch = /position\s+(\d+)/i.exec(message);
      if (positionMatch) {
        const pos = Number(positionMatch[1]);
        const before = form.advanced_json.slice(0, pos);
        const line = before.split('\n').length;
        const column = (before.split('\n').pop()?.length ?? 0) + 1;
        errors.advanced_json = `Invalid JSON near line ${line}, column ${column}: ${message}`;
      } else {
        errors.advanced_json = `Invalid JSON: ${message}`;
      }
    }
  }

  return errors;
}

export function firstParticipationError(errors: ParticipationFieldErrors): string | null {
  const values = Object.values(errors).filter(Boolean);
  return values[0] ?? null;
}
